/********************************************************************************
 * Copyright (c) 2020 Cedalo GmbH
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *
 ********************************************************************************/
/* global document */

const { looppath } = require('@cedalo/commons');
const JSG = require('../../JSG');
const Node = require('./Node');
const ItemAttributes = require('../attr/ItemAttributes');
const FormatAttributes = require('../attr/FormatAttributes');
const StreamSheetContainer = require('./StreamSheetContainer');
const Expression = require('../expr/Expression');
const StreamSheetModel = require('./StreamSheetModel');
const SheetName = require('./SheetName');
const Arrays = require('../../commons/Arrays');

const sortById = (ids) => {
	const indexOf = (id) => {
		const idx = ids.indexOf(id);
		// sort unknown indices to the end
		return idx < 0 ? ids.length - 1 : idx;
	};
	return (l, r) => indexOf(l.properties.sheetId) - indexOf(r.properties.sheetId);
};

class SheetNames {
	constructor() {
		this._names = [];
	}

	get names() {
		return this._names;
	}

	clear() {
		this._names = [];
	}

	getOrCreate(name) {
		const nameObj = this._names.find((lname) => lname.getName() === name);
		return nameObj || this.add(new SheetName(name));
	}

	get(name) {
		return this._names.find((lname) => lname.getName() === name);
	}

	add(name) {
		const index = this._names.findIndex((lname) => lname.getName() === name._name);

		if (index === -1) {
			this._names.push(name);
		} else {
			this._names[index] = name;
		}
		name.evaluate();
		return name;
	}

	delete(name) {
		Arrays.remove(this._names, name);
	}

	copy() {

	}
}

JSG.sheetNames = new SheetNames();

/**
 * Class to define a process container. The process container contains all ProcessSheetContainers
 * @type {module.StreamSheetsContainer}
 */
module.exports = class StreamSheetsContainer extends Node {
	constructor() {
		super();

		this.getFormat().setLineStyle(FormatAttributes.LineStyle.NONE);
		this.getItemAttributes().setSelectionMode(ItemAttributes.SelectionMode.NONE);
		this.getItemAttributes().setPortMode(ItemAttributes.PortMode.NONE);
		this.getItemAttributes().setClipChildren(true);
		this.getFormat().setFillColor(JSG.theme.graph);
	}

	initModel() {
		this._streamSheets = [];
		JSG.streamSheets = this._streamSheets;
		JSG.streamSheetsContainer = this;
	}

	newInstance() {
		return new StreamSheetsContainer();
	}

	init() {
		this._leftSheetContainer = this.addItem(StreamSheetContainer.create());
		this._leftSheetContainer._position = 'left';
		// splitter
		// this._splitter = new Node();
		// this._splitter.getFormat().setLineStyle(FormatAttributes.LineStyle.NONE);
		// this._splitter.setDirection(ItemAttributes.Direction.VERTICAL);
		// this.addItem(this._splitter);
		this._rightSheetContainer = this.addItem(StreamSheetContainer.create());
		this._rightSheetContainer._position = 'right';
		// this._splitter.setItemToResize(this._rightSheetContainer);
	}

	get leftSheetContainer() {
		return this._leftSheetContainer;
	}

	get rightSheetContainer() {
		return this._rightSheetContainer;
	}

	get splitter() {
		return this._splitter;
	}

	get machineContainer() {
		return this.getParent();
	}

	static get STREAMSHEET_ADDED_NOTIFICATION() {
		return 'streamsheet_added_notification';
	}

	static get STREAMSHEET_DELETED_NOTIFICATION() {
		return 'streamsheet_deleted_notification';
	}

	find(streamSheetId) {
		return this._streamSheets.find((streamSheet) => streamSheet.id === streamSheetId);
	}

	findIndex(streamSheetId) {
		return this._streamSheets.findIndex((streamSheet) => streamSheet.id === streamSheetId);
	}

	updateTree(sheetContainer, streamSheetId, properties) {
		if (sheetContainer.properties.sheetId !== streamSheetId) {
			return;
		}
		const inboxContainer = sheetContainer.getInboxContainer();
		const list = inboxContainer.getMessageListItems();

		list.updateLevels();

		if (properties.currentMessage) {
			const item = list.getTreeItemById(properties.currentMessage.id);
			if (item && item.id) {
				item.disabled = properties.currentMessage.isProcessed;
				list.setSelection('global', new Expression(item.id.toString()));
			} else {
				list.removeSelection('global');
			}
		}
		const loop = properties.loop;
		const payload = inboxContainer.getMessageTreeItems();
		if (payload && loop.enabled && loop.currentPath) {
			const item = payload.getItemByPath(looppath.parse(loop.currentPath));
			if (item && item.id) {
				payload.setSelection('global', new Expression(item.id.toString()));
			}
		}
	}

	updateStat(sheetContainer, streamSheetId, stats) {
		if (sheetContainer.properties.sheetId !== streamSheetId) {
			return;
		}

		const id = `${sheetContainer._position}Steps`;
		if (id === 'leftSteps') {
			if (!this._stepsLeftElement) {
				this._stepsLeftElement = document.getElementById(id);
			}
			if (this._stepsLeftElement) {
				this._stepsLeftElement.innerText = `${JSG.getLocalizedString('Step')} ${stats.steps}`;
			}
		} else {
			if (!this._stepsRightElement) {
				this._stepsRightElement = document.getElementById(id);
			}
			if (this._stepsRightElement) {
				this._stepsRightElement.innerText = `${JSG.getLocalizedString('Step')} ${stats.steps}`;
			}
		}
	}

	updateShape(sheetContainer, streamSheetId, shapes) {
		if (sheetContainer.properties.sheetId !== streamSheetId) {
			return;
		}
		const sheetItem = sheetContainer.getStreamSheet();
		sheetItem.setShapes(shapes);
	}

	updateTrees(streamSheetId, properties) {
		// ugly -> refactor
		this.updateTree(this.leftSheetContainer, streamSheetId, properties);
		this.updateTree(this.rightSheetContainer, streamSheetId, properties);
	}

	updateStats(streamSheetId, stats) {
		// ugly -> refactor
		this.updateStat(this.leftSheetContainer, streamSheetId, stats);
		this.updateStat(this.rightSheetContainer, streamSheetId, stats);
	}

	updateInboxContainers(streamSheetId, inbox, loop) {
		// ugly -> refactor
		this.updateInboxContainer(this.leftSheetContainer, streamSheetId, inbox, loop);
		this.updateInboxContainer(this.rightSheetContainer, streamSheetId, inbox, loop);
	}

	updateInboxContainer(sheetContainer, streamSheetId, { split, width }, loop) {
		if (sheetContainer.properties.sheetId !== streamSheetId) {
			return;
		}
		const modelProperties = sheetContainer.model.properties;
		if (split != null) {
			const inboxSplit = Number(split);
			modelProperties.inbox.split = inboxSplit;
			sheetContainer.getInboxContainer().getMessageList().setHeight(inboxSplit);
		}
		if (width != null) {
			const inboxWidth = Number(width);
			modelProperties.inbox.width = inboxWidth;
			sheetContainer.getInboxContainer().setWidth(inboxWidth);
		}

		if (loop) {
			const { enabled, path } = loop;
			modelProperties.loop.path = path;
			modelProperties.loop.enabled = enabled;
			sheetContainer
				.getInboxContainer()
				.getMessageTreeItems()
				.getTreeItemAttributes()
				.setActiveElement(enabled ? path : '');
		}
	}

	updateShapes(streamSheetId, shapes) {
		this.updateShape(this.leftSheetContainer, streamSheetId, shapes);
		this.updateShape(this.rightSheetContainer, streamSheetId, shapes);
	}

	setStreamSheetsOrder(ids) {
		if (this.streamSheetsModel) this.streamSheetsModel.sort(sortById(ids));
	}

	createStreamSheet(serverSheet) {
		const model = new StreamSheetModel();
		model.fromServer(serverSheet, true);
		this._streamSheets.push(model);
		JSG.NotificationCenter.getInstance().send(
			new JSG.Notification(StreamSheetsContainer.STREAMSHEET_ADDED_NOTIFICATION)
		);
		return model;
	}

	deleteStreamSheet(sheetId) {
		const sheet = this.find(sheetId);
		const index = sheet ? this._streamSheets.indexOf(sheet) : -1;
		if (index > -1) {
			// Arrays.remove(this._streamSheets, sheet);
			this._streamSheets.splice(index, 1);
			JSG.NotificationCenter.getInstance().send(
				new JSG.Notification(StreamSheetsContainer.STREAMSHEET_DELETED_NOTIFICATION, { index })
			);
		}
	}

	readStreamSheets(serverSheets) {
		this.initModel();

		// create all sheets
		serverSheets.forEach((serverSheet) => {
			const model = new StreamSheetModel();
			model.fromServer(serverSheet, false);
			this._streamSheets.push(model);
		});

		this._streamSheets.forEach((streamSheet, index) => {
			const { cells, shapes, names, properties } = serverSheets[index].sheet;
			shapes.shapes = shapes.shapes.filter((s) => !!s);
			streamSheet.updateSheet(cells, shapes, names, properties);
			streamSheet.updateInbox(serverSheets[index].inbox);
		});
	}

	getViewInfo(container, sheetItem, viewer) {
		if (!viewer) {
			return undefined;
		}

		const controller = viewer.getGraphController().getControllerByModelId(sheetItem.getId());
		const sheetView = controller.getView();
		const name = JSG.streamSheets[container.activeSheetIndex].name;
		container._viewInfo[name] = sheetView.getScrollOffset();

		return sheetView;
	}

	setViewInfo(container, sheetItem, sheetView, viewer) {
		if (!viewer || !sheetView) {
			return;
		}
		const info = container._viewInfo[container.model.name];
		const scrollView = sheetView.getScrollView();
		sheetItem.layout();
		scrollView.layout();
		if (info) {
			scrollView.setScrollPosition(-info.x, -info.y);
			scrollView.layout();
		} else {
			scrollView.setScrollPosition(0, 0);
			scrollView.layout();
		}
	}

	modelToStreamSheetItem(container, index, viewer, initial = false) {
		const sheetModel = this._streamSheets[index];
		const sheetItem = container.getStreamSheet();
		const graph = sheetItem.getGraph();

		if (viewer && !initial) {
			this._streamSheets[container.activeSheetIndex].sheet.selection = graph.sheetSelection.copy();
		}

		const sheetView = this.getViewInfo(container, sheetItem, viewer);

		container.getInboxContainer().getMessageListItems().jsonTree = sheetModel.messages;
		container.getInboxCaption().properties = sheetModel.properties;
		container.model = sheetModel;

		this.updateTree(container, sheetModel.id, sheetModel.properties);
		this.updateStat(container, sheetModel.id, sheetModel.properties.stats);
		this.updateInboxContainer(container, sheetModel.id, sheetModel.properties.inbox, sheetModel.properties.loop);

		sheetItem.model = sheetModel.sheet;
		sheetItem.setName(sheetModel.name);
		sheetItem.getCells()._shapesChanged = undefined;
		if (sheetModel.sheet.selection) {
			graph.sheetSelection = sheetModel.sheet.selection.copy();
			// sheetView.notifySelectionChange(viewer);
			// NotificationCenter.getInstance().send(
			// 	new Notification(WorksheetNode.SELECTION_CHANGED_NOTIFICATION, { item: sheetItem, updateFinal: true })
			// );
		}
		this.updateShape(container, sheetModel.id, sheetModel.shapes);
		this.evaluate();

		this.setViewInfo(container, sheetItem, sheetView, viewer);

		container.layout();
		sheetItem.layout();
	}

	get streamSheetsModel() {
		return this._streamSheets;
	}

	addItem(item, atIndex) {
		const ret = super.addItem(item, atIndex);

		return ret;
	}

	enumerateProcessSheets(callback) {
		this.subItems.forEach((sheet) => {
			if (sheet instanceof StreamSheetContainer) {
				callback(sheet.getStreamSheet());
			}
		});
	}

	enumerateStreamSheetContainers(callback) {
		this.subItems.forEach((sheet, index) => {
			if (sheet instanceof StreamSheetContainer) {
				callback(sheet, index);
			}
		});
	}

	findStreamSheetContainer(predicateFn) {
		return this.subItems.find(predicateFn);
	}

	getFirstStreamSheetContainer() {
		return this.subItems.find((sheet) => {
			if (sheet instanceof StreamSheetContainer) {
				return sheet;
			}
			return undefined;
		});
	}

	getFirstVisibleStreamSheetContainer() {
		return this.subItems.find((sheet) => {
			if (sheet instanceof StreamSheetContainer && sheet.isVisible()) {
				return sheet;
			}
			return undefined;
		});
	}

	isAddLabelAllowed() {
		return false;
	}

	getItemType() {
		return 'processcontainer';
	}

	_assignName(id) {
		this.setName(`StreamSheetsContainer${id}`);
	}

	assignIdsToChildren(item, lid) {
		this.subItems.forEach((subItem) => {
			if (subItem instanceof StreamSheetContainer) {
				// continue from this persisted id
				lid = subItem.getId() + 1;
			} else {
				subItem._id = lid;
				lid += 1;
			}
			lid = subItem.assignIdsToChildren(subItem, lid);
		});

		return lid;
	}

	layout() {
		super.layout();

		const split = this.machineContainer.splitView;
		const space = 150;

		const size = this.getSizeAsPoint();
		const box = new JSG.BoundingBox(split ? size.x / 2 - space / 2 : size.x, size.y);
		box.setTopLeft(0, 0);
		this.leftSheetContainer.setBoundingBoxTo(box);

		if (split) {
			box.setTopLeft(size.x / 2 + space / 2, 0);
			this.rightSheetContainer.setBoundingBoxTo(box);

			// box.setTopLeft(size.x / 2 - space / 2, 0);
			// box.setWidth(space);
			// this._splitter.setBoundingBoxTo(box);
		}
	}
};
